// PDP "Recommended / Recently viewed" tabs (sections/recommended-products.liquid).
// Products come from Rebuy, cards are the collection card (rebuy-shared.js -> rebuy-search-grid section).
// helpers from rebuy-shared.js (theme.liquid loads it in <head>, before this file)
if (!window.RebuyShared) throw new Error('[rebuy-product-tabs] rebuy-shared.js must load before rebuy-product-tabs.js');
const { getApiKey, renderCards, renderSkeletons } = window.RebuyShared;

const REBUY = 'https://rebuyengine.com/api/v1';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// rebuy.js tracks product views per visitor uuid, /products/viewed needs the same uuid
const getRebuyUuid = async () => {
  for (let i = 0; i < 50; i += 1) {
    try {
      const identity = window.Rebuy && typeof window.Rebuy.identity === 'function' && window.Rebuy.identity();
      if (identity && identity.uuid) return identity.uuid;
    } catch (error) {
      // identity not ready yet
    }
    await wait(100);
  }
  return null;
};

const fetchRebuyProducts = async ({ path, productId, uuid, limit }) => {
  const key = await getApiKey();

  const params = new URLSearchParams({ key, shopify_product_ids: productId, limit: limit + 4, fields: 'id,handle' });
  if (uuid) params.set('uuid', uuid);
  const customerId = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId;
  if (customerId) params.set('shopify_customer_id', customerId);

  const response = await fetch(`${REBUY}${path}?${params}`);
  if (!response.ok) throw new Error(`Rebuy ${path} ${response.status}`);
  const json = await response.json();
  return ((json && json.data) || []).filter((product) => String(product.id) !== String(productId));
};

// backup when rebuy is unreachable: shopify's own related products (what the old section showed)
const fetchShopifyRelated = async (url) => {
  const response = await fetch(url);
  if (!response.ok) return [];
  const json = await response.json();
  return (json && json.products) || [];
};

class RebuyProductTabs extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.limit = parseInt(this.dataset.limit, 10) || 8;
    this.tabs = [...this.querySelectorAll('[data-rpt-tab]')];
    this.count = this.querySelector('[data-rpt-count]');
    this.prev = this.querySelector('[data-rpt-prev]');
    this.next = this.querySelector('[data-rpt-next]');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.rptTab));
      tab.addEventListener('keydown', (event) => this.onTabKey(event));
    });
    this.prev.addEventListener('click', () => this.page(-1));
    this.next.addEventListener('click', () => this.page(1));
    this.querySelectorAll('[data-rpt-track]').forEach((track) => {
      track.addEventListener('scroll', () => this.scheduleNav(), { passive: true });
    });
    this.onResize = () => this.updateNav();
    window.addEventListener('resize', this.onResize);

    // only load when the section comes close to the screen
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(this);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.navFrame);
  }

  panel(name) {
    return this.querySelector(`[data-rpt-panel="${name}"]`);
  }

  track(name) {
    return this.panel(name).querySelector('[data-rpt-track]');
  }

  async load() {
    renderSkeletons(this.track('recommended'), Math.min(this.limit, 4));
    // only recently viewed needs the visitor uuid, recommended (and its shopify fallback) starts right away
    await Promise.all([this.loadRecommended(), getRebuyUuid().then((uuid) => this.loadViewed(uuid))]);

    // nothing to show at all -> hide the whole section
    const anyTab = this.tabs.some((tab) => !tab.hidden);
    this.hidden = !anyTab;
    if (anyTab) this.select(this.tabs.find((tab) => !tab.hidden).dataset.rptTab);
  }

  async loadRecommended() {
    let products = [];
    try {
      products = await fetchRebuyProducts({
        path: '/products/recommended',
        productId: this.productId,
        limit: this.limit,
      });
    } catch (error) {
      console.warn('[rebuy-tabs] recommended failed, using shopify related products', error);
    }
    if (!products.length) products = await fetchShopifyRelated(this.dataset.fallbackUrl).catch(() => []);

    const rendered = await renderCards(this.track('recommended'), products, { limit: this.limit });
    this.tabFor('recommended').hidden = !rendered;
  }

  async loadViewed(uuid) {
    let products = [];
    try {
      if (uuid) {
        products = await fetchRebuyProducts({
          path: '/products/viewed',
          productId: this.productId,
          uuid,
          limit: this.limit,
        });
      }
    } catch (error) {
      console.warn('[rebuy-tabs] recently viewed failed', error);
    }
    // first visit has no history yet -> no tab
    const rendered = products.length ? await renderCards(this.track('viewed'), products, { limit: this.limit }) : 0;
    this.tabFor('viewed').hidden = !rendered;
  }

  tabFor(name) {
    return this.tabs.find((tab) => tab.dataset.rptTab === name);
  }

  select(name) {
    this.active = name;
    this.tabs.forEach((tab) => {
      const selected = tab.dataset.rptTab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      this.panel(tab.dataset.rptTab).hidden = !selected;
    });
    this.updateNav();
  }

  // arrow keys move between tabs (WAI-ARIA tabs pattern)
  onTabKey(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const visible = this.tabs.filter((tab) => !tab.hidden);
    const index = visible.indexOf(event.currentTarget);
    const nextTab = visible[(index + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length];
    this.select(nextTab.dataset.rptTab);
    nextTab.focus();
  }

  // desktop carousel: one "page" = the visible width of the track
  page(direction) {
    const track = this.track(this.active);
    // smooth scrolling comes from css (scroll-behavior), so it also respects reduced motion
    track.scrollLeft += direction * track.clientWidth;
    this.updateNav();
  }

  // scroll fires many times per second: update the nav once per frame
  scheduleNav() {
    if (this.navFrame) return;
    this.navFrame = requestAnimationFrame(() => {
      this.navFrame = null;
      this.updateNav();
    });
  }

  updateNav() {
    if (!this.active) return;
    const track = this.track(this.active);
    const pages = Math.max(1, Math.ceil((track.scrollWidth - 1) / Math.max(track.clientWidth, 1)));
    // buttons follow the real scroll position, the counter only labels it
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    const current = atEnd ? pages : Math.min(pages, Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)) + 1);
    this.count.textContent = `${current}/${pages}`;
    this.prev.disabled = atStart;
    this.next.disabled = atEnd;
    this.toggleAttribute('data-single-page', pages <= 1);
  }
}

if (!customElements.get('rebuy-product-tabs')) {
  customElements.define('rebuy-product-tabs', RebuyProductTabs);
}
